import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import {
  BookOpen,
  PlayCircle,
  Layers,
  Settings,
  SlidersHorizontal,
  BarChart3,
  User,
  ChevronRight,
  ChevronLeft,
  LogOut,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
  path?: string;
}

const navItems: NavItem[] = [
  { id: "simulations", label: "Simulações", icon: <PlayCircle className="w-6 h-6" />, path: "/simulacoes" },
  { id: "parameters", label: "Parâmetros", icon: <SlidersHorizontal className="w-6 h-6" />, path: "/parametros" },
  { id: "catalog", label: "Catálogo", icon: <BookOpen className="w-6 h-6" />, path: "/catalog" },
  { id: "materiality", label: "Materialidade", icon: <Layers className="w-6 h-6" />, path: "/materialidades" },
];

const bottomNavItems: NavItem[] = [
  {
    id: "settings",
    label: "Configurações",
    icon: <Settings className="w-6 h-6" />,
  },
  {
    id: "profile",
    label: "Perfil",
    icon: <User className="w-6 h-6" />,
  },
];

export function Navigation() {
  const [expanded, setExpanded] = useState(() => {
    const saved = localStorage.getItem("puzzle_nav-expanded");
    return saved !== null ? saved === "true" : true;
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const isSelected = (path?: string) => (path ? location.pathname.startsWith(path) : false);

  useEffect(() => {
    localStorage.setItem("puzzle_nav-expanded", String(expanded));
  }, [expanded]);

  return (
    <aside
      className={cn(
        "bg-[#0D1415] flex flex-col transition-all duration-300 h-screen sticky top-0",
        expanded ? "w-[220px] min-w-[220px] max-w-[360px]" : "w-24"
      )}
    >
      {/* Logo */}
      <div className={cn(
        "flex items-center",
        expanded ? "py-7 px-5" : "py-7 px-5 justify-center"
      )}>
        {expanded ? (
          <svg width="136" height="48" viewBox="0 0 136 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M27.3328 34.6693L22.8637 35.8668C22.2607 36.0283 21.67 35.9422 21.0916 35.6083C20.5133 35.2744 20.1433 34.8059 19.9817 34.2029L18.8286 29.8991C19.6055 29.6317 20.1942 29.1439 20.5949 28.4357C20.9957 27.7274 21.0896 26.9759 20.8767 26.1812C20.6637 25.3863 20.2065 24.7823 19.5052 24.3689C18.8041 23.9557 18.0499 23.8261 17.2425 23.98L16.0845 19.6582C15.9216 19.0501 16.0072 18.4574 16.3414 17.8801C16.6756 17.303 17.1442 16.9336 17.7472 16.7721L21.9471 15.6467C21.9021 14.8164 22.0924 14.061 22.518 13.3807C22.9438 12.7005 23.5384 12.2581 24.3021 12.0535C25.0656 11.849 25.8022 11.9357 26.5121 12.3139C27.222 12.6922 27.7644 13.25 28.1394 13.9875L32.292 12.8748C32.9 12.7119 33.4926 12.7969 34.0695 13.1299C34.6464 13.4631 35.0163 13.9337 35.1792 14.5417L36.2919 18.6943C37.1222 18.6492 37.8719 18.8535 38.541 19.307C39.21 19.7608 39.6468 20.3694 39.8514 21.1329C40.056 21.8965 39.9756 22.6222 39.6102 23.3099C39.2447 23.9977 38.6932 24.529 37.9557 24.9039L39.0765 29.0865C39.238 29.6895 39.1525 30.28 38.8198 30.858C38.4869 31.4361 38.0165 31.8066 37.4084 31.9695L32.921 33.1719C32.6718 32.2566 32.1938 31.5703 31.487 31.1128C30.7802 30.6553 30.0413 30.5299 29.2703 30.7365C28.4995 30.943 27.9228 31.421 27.5402 32.1706C27.1574 32.9202 27.0882 33.7531 27.3328 34.6693ZM22.3004 33.5146L24.8747 32.8248C25.0004 31.6578 25.4205 30.6922 26.135 29.9279C26.8495 29.1638 27.6871 28.6531 28.6479 28.3957C29.5961 28.1416 30.5767 28.1651 31.5897 28.4663C32.6028 28.7677 33.4626 29.3904 34.1691 30.3344L36.7201 29.6509L35.0628 23.4656C34.9725 23.1289 35.0317 23.3496 34.9725 23.1289C34.8884 22.8149 34.8757 22.7674 34.8008 22.4881C34.726 22.2087 34.7201 22.187 34.6136 21.7895C34.5519 21.559 34.5519 21.559 34.5388 21.5104L32.8563 15.2312L26.6489 16.8945C26.5285 16.9267 26.6489 16.8945 26.3694 16.9693C26.2146 17.0108 25.9504 17.0816 25.6711 17.1565C25.3918 17.2313 25.2727 17.2632 24.9728 17.3436C24.6729 17.4239 24.9587 17.3474 24.6933 17.4184L18.4367 19.0949L19.1198 21.6444C20.102 21.9052 20.9646 22.3753 21.7074 23.0545C22.4503 23.7338 22.9543 24.5682 23.2195 25.5579C23.4819 26.5372 23.4628 27.5074 23.1623 28.4687C22.8618 29.4298 22.3464 30.2605 21.6162 30.961L22.3004 33.5146Z" fill="#BAC9CA"/>
            <path d="M113.317 32.8552C112.004 32.8552 110.871 32.5825 109.916 32.037C108.968 31.4859 108.237 30.7075 107.726 29.7018C107.215 28.6904 106.959 27.5001 106.959 26.1307C106.959 24.7842 107.215 23.6023 107.726 22.5853C108.243 21.5626 108.965 20.7671 109.891 20.1989C110.817 19.6251 111.905 19.3381 113.155 19.3381C113.962 19.3381 114.723 19.4688 115.439 19.7302C116.161 19.9859 116.797 20.3836 117.348 20.9234C117.905 21.4631 118.343 22.1506 118.661 22.9859C118.979 23.8154 119.138 24.804 119.138 25.9518V26.8978H108.408V24.8182H116.181C116.175 24.2273 116.047 23.7018 115.797 23.2415C115.547 22.7756 115.198 22.4092 114.749 22.1421C114.306 21.8751 113.789 21.7415 113.198 21.7415C112.567 21.7415 112.013 21.895 111.536 22.2018C111.058 22.5029 110.686 22.9006 110.419 23.395C110.158 23.8836 110.024 24.4205 110.019 25.0057V26.8211C110.019 27.5825 110.158 28.2359 110.436 28.7813C110.715 29.3211 111.104 29.7359 111.604 30.0256C112.104 30.3097 112.689 30.4518 113.36 30.4518C113.808 30.4518 114.215 30.3893 114.578 30.2643C114.942 30.1336 115.257 29.9432 115.524 29.6932C115.791 29.4432 115.993 29.1336 116.129 28.7643L119.01 29.0881C118.828 29.8495 118.482 30.5143 117.97 31.0825C117.465 31.645 116.817 32.0825 116.027 32.395C115.237 32.7018 114.334 32.8552 113.317 32.8552Z" fill="#BAC9CA"/>
            <path d="M105.069 15.1449V32.5994H101.983V15.1449H105.069Z" fill="#BAC9CA"/>
            <path d="M89.0566 32.5995V30.6392L95.8066 22.1762V22.0654H89.2782V19.5085H99.5822V21.6137L93.1561 29.9318V30.0426H99.8038V32.5995H89.0566Z" fill="#BAC9CA"/>
            <path d="M76.2998 32.5995V30.6392L83.0498 22.1762V22.0654H76.5214V19.5085H86.8254V21.6137L80.3992 29.9318V30.0426H87.047V32.5995H76.2998Z" fill="#BAC9CA"/>
            <path d="M70.9326 27.0938V19.5085H74.0178V32.5995H71.0264V30.2727H70.89C70.5946 31.0057 70.1088 31.6051 69.4326 32.071C68.7622 32.537 67.9355 32.7699 66.9525 32.7699C66.0945 32.7699 65.336 32.5796 64.6769 32.1989C64.0235 31.8125 63.5122 31.2529 63.1428 30.5199C62.7735 29.7813 62.5889 28.8892 62.5889 27.8438V19.5085H65.6741V27.3665C65.6741 28.196 65.9014 28.8551 66.3559 29.3438C66.8105 29.8324 67.4071 30.0767 68.1457 30.0767C68.6002 30.0767 69.0406 29.9659 69.4667 29.7443C69.8928 29.5227 70.2423 29.1932 70.515 28.7557C70.7934 28.3125 70.9326 27.7585 70.9326 27.0938Z" fill="#BAC9CA"/>
            <path d="M48 32.5994V15.1449H54.5455C55.8864 15.1449 57.0114 15.3949 57.9205 15.8949C58.8352 16.3949 59.5256 17.0824 59.9915 17.9574C60.4631 18.8267 60.6989 19.8154 60.6989 20.9233C60.6989 22.0426 60.4631 23.0369 59.9915 23.9063C59.5199 24.7756 58.8239 25.4602 57.9034 25.9602C56.983 26.4546 55.8494 26.7017 54.5028 26.7017H50.1648V24.1023H54.0767C54.8608 24.1023 55.5028 23.9659 56.0028 23.6932C56.5028 23.4205 56.8722 23.0455 57.1108 22.5682C57.3551 22.0909 57.4773 21.5426 57.4773 20.9233C57.4773 20.304 57.3551 19.7585 57.1108 19.2869C56.8722 18.8154 56.5 18.4489 55.9943 18.1875C55.4943 17.9205 54.8494 17.7869 54.0597 17.7869H51.1619V32.5994H48Z" fill="#BAC9CA"/>
          </svg>
        ) : (
          <div className="w-12 h-12 flex items-center justify-center">
            <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.3328 22.7235L6.86366 23.921C6.26066 24.0825 5.66998 23.9964 5.09163 23.6625C4.51328 23.3286 4.14332 22.8601 3.98174 22.2571L2.82855 17.9533C3.60547 17.6859 4.19424 17.1981 4.59487 16.4899C4.99568 15.7816 5.08961 15.0301 4.87667 14.2354C4.66368 13.4405 4.20653 12.8365 3.5052 12.4231C2.80409 12.0099 2.04986 11.8803 1.24252 12.0342L0.0844932 7.71236C-0.0784355 7.1043 0.00719575 6.51162 0.341387 5.93432C0.675625 5.35719 1.14424 4.98783 1.74724 4.82626L5.94714 3.7009C5.90207 2.87057 6.09237 2.11524 6.51804 1.4349C6.94376 0.754741 7.53845 0.312349 8.3021 0.10773C9.06557 -0.0968424 9.80223 -0.0100621 10.5121 0.368069C11.222 0.746374 11.7644 1.30424 12.1394 2.04168L16.292 0.92901C16.9 0.766081 17.4926 0.851103 18.0695 1.18407C18.6464 1.51726 19.0163 1.98789 19.1792 2.59595L20.2919 6.74848C21.1222 6.70341 21.8719 6.90765 22.541 7.3612C23.21 7.81497 23.6468 8.42359 23.8514 9.18706C24.056 9.95071 23.9756 10.6764 23.6102 11.3641C23.2447 12.0519 22.6932 12.5832 21.9557 12.9581L23.0765 17.1407C23.238 17.7437 23.1525 18.3342 22.8198 18.9122C22.4869 19.4903 22.0165 19.8608 21.4084 20.0237L16.921 21.2261C16.6718 20.3108 16.1938 19.6245 15.487 19.167C14.7802 18.7095 14.0413 18.5841 13.2703 18.7907C12.4995 18.9972 11.9228 19.4752 11.5402 20.2248C11.1574 20.9744 11.0882 21.8073 11.3328 22.7235Z" fill="#BAC9CA"/>
            </svg>
          </div>
        )}
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 w-full flex flex-col pt-11 overflow-y-auto">
        <div className="flex flex-col gap-1 mb-10">
          {navItems.map((item) => (
            <button
              key={item.id}
              className={cn(
                "relative flex items-center gap-3 rounded-full transition-colors",
                expanded ? "px-4 py-4 mx-5" : "h-16 flex-col justify-center",
                isSelected(item.path) && "bg-[#135045]"
              )}
              onClick={() => item.path && navigate(item.path)}
            >
              {expanded ? (
                <>
                  <div className={cn(
                    "shrink-0",
                    isSelected(item.path) ? "text-[#88C0B2]" : "text-[#DCE3E4]"
                  )}>
                    {item.icon}
                  </div>
                  <span className={cn(
                    "text-sm font-medium whitespace-nowrap",
                    isSelected(item.path) ? "text-[#88C0B2]" : "text-[#DCE3E4]"
                  )}>
                    {item.label}
                  </span>
                  {item.badge !== undefined && (
                    <Badge className="ml-auto h-[6px] w-[6px] min-w-[6px] p-0 rounded-full bg-[#BA1A1A] border-0" />
                  )}
                </>
              ) : (
                <>
                  <div className="relative flex items-center justify-center w-14 h-14">
                    <div className={cn(
                      "relative",
                      isSelected(item.path) ? "text-[#88C0B2]" : "text-[#DCE3E4]"
                    )}>
                      {item.icon}
                    </div>
                    {item.badge !== undefined && (
                      <Badge className="absolute top-0 right-0 h-[6px] w-[6px] min-w-[6px] p-0 rounded-full bg-[#BA1A1A] border-0" />
                    )}
                  </div>
                </>
              )}
            </button>
          ))}
        </div>

        {/* Bottom Navigation */}
        <div className="flex-1 flex flex-col justify-end pb-5">
          <div className={cn("flex flex-col gap-1", expanded ? "px-5" : "")}>
            {bottomNavItems.map((item) => (
              <button
                key={item.id}
                className={cn(
                  "relative flex items-center gap-3 rounded-full transition-colors hover:bg-[#1A2425]",
                  expanded ? "px-4 py-4" : "h-16 flex-col justify-center"
                )}
              >
                {expanded ? (
                  <>
                    <div className="shrink-0 text-[#DCE3E4]">
                      {item.icon}
                    </div>
                    <span className="text-sm font-medium text-[#DCE3E4] whitespace-nowrap">
                      {item.label}
                    </span>
                  </>
                ) : (
                  <div className="flex items-center justify-center w-14 h-14">
                    <div className="text-[#DCE3E4]">
                      {item.icon}
                    </div>
                  </div>
                )}
              </button>
            ))}
            
            {/* User info and logout */}
            {expanded && user && (
              <div className="px-4 py-3 mt-2 border-t border-[#1A2425]">
                <div className="text-xs text-[#BAC9CA] mb-2">
                  {user.email}
                </div>
              </div>
            )}
            
            <button
              onClick={logout}
              className={cn(
                "relative flex items-center gap-3 rounded-full transition-colors hover:bg-[#1A2425] text-[#BAC9CA] hover:text-[#DCE3E4]",
                expanded ? "px-4 py-4" : "h-16 flex-col justify-center"
              )}
            >
              {expanded ? (
                <>
                  <div className="shrink-0">
                    <LogOut className="w-6 h-6" />
                  </div>
                  <span className="text-sm font-medium whitespace-nowrap">
                    Sair
                  </span>
                </>
              ) : (
                <div className="flex items-center justify-center w-14 h-14">
                  <LogOut className="w-6 h-6" />
                </div>
              )}
            </button>
          </div>

          {/* Toggle Button */}
          <div className={cn("mt-2", expanded ? "px-5" : "")}>
            <button
              className={cn(
                "flex items-center justify-center rounded-full transition-colors hover:bg-[#1A2425]",
                expanded ? "w-14 h-14" : "h-16 w-full"
              )}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <ChevronLeft className="w-6 h-6 text-[#DCE3E4]" />
              ) : (
                <ChevronRight className="w-6 h-6 text-[#DCE3E4]" />
              )}
            </button>
          </div>
        </div>
      </nav>
    </aside>
  );
}
